import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Contact } from "@/types/contacts";
import { BaseEntity } from "@/types/entities";

type ImportData = Contact | BaseEntity;

export type ImportMode = 'new-only' | 'upsert' | 'cancel';

interface ImportConfirmDialogProps<T extends ImportData> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newItems: T[];
  existingItems: T[];
  duplicates: T[];
  onConfirm: (mode: ImportMode) => void;
  entityName: string;
}

export function ImportConfirmDialog<T extends ImportData>({
  open,
  onOpenChange,
  newItems,
  existingItems,
  duplicates,
  onConfirm,
  entityName,
}: ImportConfirmDialogProps<T>) {
  const totalItems = newItems.length + existingItems.length;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Confirmar Importação</AlertDialogTitle>
          <AlertDialogDescription>
            Análise dos dados a serem importados:
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col items-center p-4 border rounded-lg bg-success/10">
              <span className="text-3xl font-bold text-success">{newItems.length}</span>
              <span className="text-sm text-muted-foreground">Novos</span>
            </div>
            <div className="flex flex-col items-center p-4 border rounded-lg bg-warning/10">
              <span className="text-3xl font-bold text-warning">{existingItems.length}</span>
              <span className="text-sm text-muted-foreground">Existentes</span>
            </div>
            <div className="flex flex-col items-center p-4 border rounded-lg bg-muted">
              <span className="text-3xl font-bold text-muted-foreground">{totalItems}</span>
              <span className="text-sm text-muted-foreground">Total</span>
            </div>
          </div>

          {duplicates.length > 0 && (
            <div className="p-4 border border-warning rounded-lg bg-warning/5">
              <p className="text-sm font-medium mb-2">
                ⚠️ {duplicates.length} {entityName}(s) já existem no banco:
              </p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {duplicates.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="text-xs text-muted-foreground">
                    • {'name' in item ? item.name : (item as Contact).company}
                  </div>
                ))}
                {duplicates.length > 10 && (
                  <p className="text-xs text-muted-foreground italic">
                    ... e mais {duplicates.length - 10}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <p className="text-sm font-medium">Escolha como deseja proceder:</p>
            <div className="space-y-2">
              <button
                onClick={() => onConfirm('new-only')}
                className="w-full p-3 border rounded-lg hover:bg-accent transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Adicionar apenas novos</p>
                    <p className="text-xs text-muted-foreground">
                      Ignora {existingItems.length} duplicado(s)
                    </p>
                  </div>
                  <Badge variant="outline">{newItems.length} itens</Badge>
                </div>
              </button>

              <button
                onClick={() => onConfirm('upsert')}
                className="w-full p-3 border rounded-lg hover:bg-accent transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Atualizar existentes</p>
                    <p className="text-xs text-muted-foreground">
                      Insere {newItems.length} novo(s) e atualiza {existingItems.length} existente(s)
                    </p>
                  </div>
                  <Badge variant="outline">{totalItems} itens</Badge>
                </div>
              </button>
            </div>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onConfirm('cancel')}>
            Cancelar
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
